import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { logout } from '../services/authService';
import Header from './Header';
import Badge from '@mui/material/Badge';
import { isAuthenticated } from '../services/authService';
import './Dashboard.css';
import { useEvents } from '../contexts/EventContext.js'; // Import useEvents
import { useUser } from '../contexts/UserContext.js'; // Import useUser
import { useTenant } from '../contexts/TenantContext.js'; // Import useTenant
import EventCardV2 from './EventCardV2'; // Import EventCard
import EventCardV3 from './EventCardV3'; // Import EventCardV3
import TenantGuard from './TenantGuard'; // Import TenantGuard

// Import Material UI components that are still used directly in Dashboard.js
import Grid from '@mui/material/Grid'; 
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box'; // Import Box for layout
import Button from '@mui/material/Button'; // For filter buttons
import ButtonGroup from '@mui/material/ButtonGroup'; // For grouped filter buttons
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'; // For participation filter
import StarOutlineIcon from '@mui/icons-material/StarOutline'; // For rating filter
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'; // For all filter
import Menu from '@mui/material/Menu'; // For mobile dropdown menu
import MenuItem from '@mui/material/MenuItem'; // For mobile dropdown menu items
import FilterListIcon from '@mui/icons-material/FilterList'; // Filter icon for mobile dropdown button
import useMediaQuery from '@mui/material/useMediaQuery'; // For responsive design
import { useTheme } from '@mui/material/styles'; // To access the theme breakpoints

const Dashboard = () => {
  const { t, i18n } = useTranslation(); // Initialize useTranslation, include i18n instance
  const initialLoadPerformed = useRef(false); // Ref to track initial load
  const [activeFilter, setActiveFilter] = useState('allEvents'); // State for active filter: 'allEvents', 'upcomingEvents', 'pastEvents'
  // Add state for mobile dropdown menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Access theme and determine if we're on a mobile device
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const { tenant_id } = useParams(); // Extract tenant_id from URL

  const { 
    userEvents, 
    loadingEvents, 
    errorEvents, 
    fetchUserEventsV2: contextFetchUserEventsV2 
  } = useEvents();

  const { 
    userData, 
    loadingUser, 
    errorUser, 
    fetchUserData: contextFetchUserData, 
    clearUserData // Added clearUserData from context
  } = useUser();

  const {
    tenantData,
    errorTenant
  } = useTenant();

  // Calculate filtered events based on activeFilter - define this early to avoid conditional hook calls
  const getFilteredEvents = useCallback(() => {
    if (!userEvents) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Compare dates only, not time

    switch(activeFilter) {
      case 'upcomingEvents':
        // Future events
        return userEvents.filter(event => {
          const eventDate = event.start_time ? new Date(event.start_time) : null;
          return eventDate && eventDate >= today;
        });
      case 'pastEvents':
        // Past events
        return userEvents.filter(event => {
          const eventDate = event.start_time ? new Date(event.start_time) : null;
          return eventDate && eventDate < today;
        });
      case 'allEvents':
      default:
        return userEvents; // Show all events
    }
  }, [userEvents, activeFilter]);
  
  // Calculate counts for badges on filter buttons - define early to avoid conditional hooks
  const futureEventsNoAnswerCount = userEvents
    ? userEvents.filter(event => {
        const eventDate = event.start_time ? new Date(event.start_time) : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return eventDate && eventDate >= today && event.event_user_state?.answer == null;
      }).length
    : 0;
  
  const pastEventsNeedRatingCount = userEvents
    ? userEvents.filter(event => {
        const eventDate = event.start_time ? new Date(event.start_time) : null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return eventDate && 
               eventDate < today
      }).length
    : 0;

  // Now we can compute filteredEvents - no conditional hooks after this point
  const filteredEvents = getFilteredEvents();

  // Helper function to format date (remains in Dashboard as it's passed to EventCard)
  const formatDate = (dateString) => {
    if (!dateString) return t('eventCard.formatDateNA'); // Use translation key for N/A
    try {
      const date = new Date(dateString);
      const formattedDate = new Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(date);
      
      // Capitalize the first letter of the formatted string
      if (formattedDate && formattedDate.length > 0) {
        return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
      }
      return formattedDate; // Fallback for empty string, though unlikely
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Fallback to original string in case of error
    }
  };

  useEffect(() => {
    console.log('Dashboard useEffect triggered to check authentication and fetch data.');
    if (!isAuthenticated()) {
      if (localStorage.getItem("LOGIN_PAGE")) {
        const loginPage = localStorage.getItem("LOGIN_PAGE");
        console.log("Redirecting to saved login page:", `${loginPage}login`);
        navigate(`${loginPage}login`);
        return;
      }
      console.log("Redirecting to default login page");
      navigate(`/app/${tenant_id}/login`); // Redirect to tenant-specific login if not authenticated
      return;
    }

    // The initial data fetch is now fully managed by the contexts (UserContext, EventContext).
    // We just need to ensure the user data is fetched, and EventContext will trigger its own fetch.
    if (!initialLoadPerformed.current) {
      contextFetchUserData();
      initialLoadPerformed.current = true;
    }
  }, [navigate, contextFetchUserData, tenant_id]); // Simplified dependencies

  const handleEventClick = (eventId) => {
    console.log('Navigating to event:', { tenant_id, eventId });
    if (tenant_id && eventId) {
        navigate(`/app/${tenant_id}/event/${eventId}`);
        // navigate(`/app/${tenant_id}/dashboard`);
        // setTimeout(() => {
        //   navigate(`/app/${tenant_id}/event/${eventId}`);
        // }, 0);
    } else if (eventId) {
        console.warn(`Task ID not available for event ${eventId}, navigating to event only or a default view`);
    } else {
        console.error("Event ID is missing, cannot navigate.");
    }
  };

  // Mobile dropdown menu handlers
  const handleOpenFilterMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseFilterMenu = () => {
    setAnchorEl(null);
  };
  
  const handleFilterSelect = (filter) => {
    setActiveFilter(filter);
    handleCloseFilterMenu();
  };
  
  const getFirstEventInFuture = () => {
    const now = new Date();
    return userEvents.find(event => new Date(event.start_time) > now);
  };

  const handleScrollToItem = (targetId) => {
    // 1. Find the element by the unique ID you assigned
    const element = document.getElementById(`grid-item-${targetId}`);

    // 2. Use scrollIntoView if the element exists
    if (element) {
      // 'behavior: smooth' animates the scroll
      // 'block: center' scrolls so the element is in the middle of the viewport
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  console.log('First event in future (for debugging scroll):', getFirstEventInFuture());

  useEffect(() => {
    // On initial load, scroll to the first future event
    const timer = setTimeout(() => {
      const firstEvent = getFirstEventInFuture();
      if (firstEvent) {
        handleScrollToItem(firstEvent.event_id);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle error states
  if (errorUser) {
    return <div className="dashboard-error">{t('dashboard.errorLoadingUserData', { message: errorUser.message })}</div>;
  }
  
  if (errorTenant) {
    return <div className="dashboard-error">{t('dashboard.errorLoadingTenantData', { message: errorTenant.message })}</div>;
  }

  // Calculate count for the badge: future events with no answer (leaving this for backward compatibility)
  return (
    <TenantGuard>
      <div className="dashboard-container" >
        {/* <Header title={t('dashboard.headerTitle')} sx={{ mx: 0 }} /> */}
        
        <div className="dashboard-content">
          {userData ? (
            <div className="user-info">
              <h2 style={{
                fontSize: isMobile ? '1.1rem' : '1.5rem',
                margin: isMobile ? '0.5em 0 0.2em 0' : '0.7em 0 0.3em 0',
                fontWeight: 600,
                letterSpacing: 0.2,
                lineHeight: 1.2,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? '90vw' : '100%'
              }}>{t('dashboard.welcome', { name: userData.first_name })}</h2>
              {tenantData && tenantData.metadatas && tenantData.metadatas.welcome_message ? (
                <p>{tenantData.metadatas.welcome_message}</p>
              ) : (
                <p>{t('dashboard.defaultWelcomeMessage')}</p>
              )}
            </div>
          ) : (
            !loadingUser && <p>{t('dashboard.unableToLoadUserData')}</p> // Use loadingUser
          )}
          
          <div className="dashboard-widgets">
            <div className="widget events-widget">
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={contextFetchUserEventsV2} title={t('dashboard.refreshEvents')}>
                  <Typography variant="h5" component="h3" sx={{ margin: 0 }}>
                    {tenantData?.metadatas?.agenda_title || t('dashboard.myAgenda')}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {!isMobile && (
                    <ButtonGroup variant="outlined" aria-label="event filters">
                      <Button 
                        onClick={() => setActiveFilter('allEvents')}
                        variant={activeFilter === 'allEvents' ? 'contained' : 'outlined'}
                        startIcon={<FormatListBulletedIcon />}
                        sx={{ textTransform: 'none' }}
                        color="primary"
                      >
                        {t('dashboard.filter.allEvents')}
                      </Button>
                      
                      <Button
                        onClick={() => setActiveFilter('upcomingEvents')}
                        variant={activeFilter === 'upcomingEvents' ? 'contained' : 'outlined'}
                        startIcon={<ThumbUpOutlinedIcon />}
                        sx={{ textTransform: 'none' }}
                        color="primary"
                      >
                        <Badge
                          badgeContent={futureEventsNoAnswerCount}
                          color="primary"
                          invisible={futureEventsNoAnswerCount === 0}
                          sx={{
                            '& .MuiBadge-badge': {
                              right: -10,
                              top: -6,
                            }
                          }}
                        >
                          {t('dashboard.filter.upcomingEvents')}
                        </Badge>
                      </Button>
                      
                      <Button
                        onClick={() => setActiveFilter('pastEvents')}
                        variant={activeFilter === 'pastEvents' ? 'contained' : 'outlined'}
                        startIcon={<StarOutlineIcon />}
                        sx={{ textTransform: 'none' }}
                        color="primary"
                      >
                        <Badge
                          badgeContent={pastEventsNeedRatingCount}
                          color="primary"
                          invisible={pastEventsNeedRatingCount === 0}
                          sx={{
                            '& .MuiBadge-badge': {
                              right: -10,
                              top: -6,
                            }
                          }}
                        >
                          {t('dashboard.filter.pastEvents')}
                        </Badge>
                      </Button>
                    </ButtonGroup>
                  )}
                  
                  {isMobile && (
                    <Box>
                      <Button
                        aria-controls={open ? 'filter-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        onClick={handleOpenFilterMenu}
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        sx={{ textTransform: 'none' }}
                        color="primary"
                      >
                        {activeFilter === 'allEvents' && t('dashboard.filter.allEvents')}
                        {activeFilter === 'upcomingEvents' && (
                          <Badge
                            badgeContent={futureEventsNoAnswerCount}
                            color="primary"
                            invisible={futureEventsNoAnswerCount === 0}
                          >
                            {t('dashboard.filter.upcomingEvents')}
                          </Badge>
                        )}
                        {activeFilter === 'pastEvents' && (
                          <Badge
                            badgeContent={pastEventsNeedRatingCount}
                            color="primary"
                            invisible={pastEventsNeedRatingCount === 0}
                          >
                            {t('dashboard.filter.pastEvents')}
                          </Badge>
                        )}
                      </Button>
                      <Menu
                        id="filter-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleCloseFilterMenu}
                        MenuListProps={{
                          'aria-labelledby': 'filter-button',
                        }}
                      >
                        <MenuItem 
                          onClick={() => handleFilterSelect('allEvents')}
                          selected={activeFilter === 'allEvents'}
                          sx={{ '&.Mui-selected': { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }, '&:hover': { backgroundColor: theme.palette.primary.light } }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormatListBulletedIcon fontSize="small" sx={{ mr: 1 }} />
                            {t('dashboard.filter.allEvents')}
                          </Box>
                        </MenuItem>
                        <MenuItem 
                          onClick={() => handleFilterSelect('upcomingEvents')}
                          selected={activeFilter === 'upcomingEvents'}
                          sx={{ '&.Mui-selected': { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }, '&:hover': { backgroundColor: theme.palette.primary.light } }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ThumbUpOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                            <Badge
                              badgeContent={futureEventsNoAnswerCount}
                              color="primary"
                              invisible={futureEventsNoAnswerCount === 0}
                            >
                              {t('dashboard.filter.upcomingEvents')}
                            </Badge>
                          </Box>
                        </MenuItem>
                        <MenuItem 
                          onClick={() => handleFilterSelect('pastEvents')}
                          selected={activeFilter === 'pastEvents'}
                          sx={{ '&.Mui-selected': { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }, '&:hover': { backgroundColor: theme.palette.primary.light } }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <StarOutlineIcon fontSize="small" sx={{ mr: 1 }} />
                            <Badge
                              badgeContent={pastEventsNeedRatingCount}
                              color="primary"
                              invisible={pastEventsNeedRatingCount === 0}
                            >
                              {t('dashboard.filter.pastEvents')}
                            </Badge>
                          </Box>
                        </MenuItem>
                      </Menu>
                    </Box>
                  )}
                </Box>
              </div>

              {loadingEvents && <p>{t('dashboard.loadingEvents')}</p>}
              {errorEvents && !loadingEvents && (
                <p>
                  {errorEvents.response?.status === 429
                    ? errorEvents.response.headers['retry-after']
                      ? t('dashboard.errorLoadingEventsRateLimit', { 
                          seconds: isNaN(parseInt(errorEvents.response.headers['retry-after'])) 
                                   ? Math.round((new Date(errorEvents.response.headers['retry-after']).getTime() - new Date().getTime()) / 1000)
                                   : parseInt(errorEvents.response.headers['retry-after'])
                        })
                      : t('dashboard.errorLoadingEventsRateLimitGeneric')
                    : t('dashboard.errorLoadingEvents')}
                </p>
              )}
              
              {!loadingEvents && !errorEvents && userEvents && userEvents.length > 0 ? (
                <Grid container spacing={2} className="events-grid-container" sx={{ alignItems: 'center', justifyContent: 'center', px: isMobile ? 0 : 2 }}>
                  {filteredEvents.length > 0 ? filteredEvents.map(event => (
                    <Grid item xs={12} sm={6} md={4} 
                      key={event.event_id}
                      id={`grid-item-${event.event_id}`}
                    >
                      <EventCardV3
                        event={event}
                        handleEventClick={handleEventClick}
                        formatDate={formatDate}
                      />
                    </Grid>
                  )) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" align="center" sx={{ my: 4 }}>
                        {activeFilter === 'upcomingEvents' 
                          ? t('dashboard.noUpcomingEvents')
                          : activeFilter === 'pastEvents'
                          ? t('dashboard.noPastEvents')
                          : t('dashboard.noRecentActivity')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                !loadingEvents && !errorEvents && <p>{t('dashboard.noRecentActivity')}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </TenantGuard>
  );
};

export default Dashboard;
